import i18n from '../i18n';

/**
 * Get the current locale from i18n
 * Using en-GB for DD/MM/YYYY format
 */
function getCurrentLocale(): string {
  const language = i18n.language;
  return language === 'ru' ? 'ru-RU' : 'en-GB'; // Changed to en-GB for DD/MM/YYYY format
}

/**
 * Format a date in DD/MM/YYYY format
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString(getCurrentLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Format a date with day name in DD/MM/YYYY format (e.g., "Mon, 25/12/2024")
 */
export function formatDateWithDay(date: Date): string {
  return date.toLocaleDateString(getCurrentLocale(), {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Format a date with full day and month names (e.g., "Monday, 25 December 2024")
 */
export function formatDateFull(date: Date): string {
  return date.toLocaleDateString(getCurrentLocale(), {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
}

/**
 * Format a time to display in user-friendly format (e.g., "3:30 PM")
 */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString(getCurrentLocale(), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: getCurrentLocale() === 'en-US'
  });
}

/**
 * Format a datetime in DD/MM/YYYY HH:MM format
 */
export function formatDateTime(date: Date): string {
  return `${formatDate(date)} ${formatTime(date)}`;
}

/**
 * Format a datetime with day name (e.g., "Mon, 25/12/2024 15:30")
 */
export function formatDateTimeWithDay(date: Date): string {
  return `${formatDateWithDay(date)} ${formatTime(date)}`;
}

/**
 * Get relative time (e.g., "2 days ago", "in 3 hours")
 */
export function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffInMs = date.getTime() - now.getTime();
  const diffInSecs = Math.floor(diffInMs / 1000);
  const diffInMins = Math.floor(diffInSecs / 60);
  const diffInHours = Math.floor(diffInMins / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays > 0) {
    return `in ${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
  } else if (diffInDays < 0) {
    return `${Math.abs(diffInDays)} day${Math.abs(diffInDays) !== 1 ? 's' : ''} ago`;
  } else if (diffInHours > 0) {
    return `in ${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`;
  } else if (diffInHours < 0) {
    return `${Math.abs(diffInHours)} hour${Math.abs(diffInHours) !== 1 ? 's' : ''} ago`;
  } else if (diffInMins > 0) {
    return `in ${diffInMins} minute${diffInMins !== 1 ? 's' : ''}`;
  } else if (diffInMins < 0) {
    return `${Math.abs(diffInMins)} minute${Math.abs(diffInMins) !== 1 ? 's' : ''} ago`;
  } else {
    return 'just now';
  }
} 